import { getElementChild, getElementChildren, getElements } from '../Utility/Dom'
import { IS_ACTIVE } from '../Utility/State'

class Tab {
  private readonly roots: NodeListOf<HTMLElement>

  constructor() {
    this.roots = getElements('tab')
  }

  public init(): void {
    this.roots.forEach((root: HTMLElement): void => this.setup(root))
  }

  private moveIndicator(indicator: HTMLElement, tab: HTMLElement): void {
    indicator.style.width = `${tab.offsetWidth}px`
    indicator.style.transform = `translate3d(${tab.offsetLeft}px, 0, 0)`
  }

  private resolveKeyTarget(key: string, index: number, total: number): null | number {
    switch (key) {
      case 'ArrowLeft':
        return (index - 1 + total) % total
      case 'ArrowRight':
        return (index + 1) % total
      case 'End':
        return total - 1
      case 'Home':
        return 0
      default:
        return null
    }
  }

  private scrollTabIntoView(tab: HTMLElement): void {
    const container = tab.parentElement
    if (container === null) return

    const tabLeft = tab.offsetLeft
    const tabRight = tabLeft + tab.offsetWidth
    const viewLeft = container.scrollLeft
    const viewRight = viewLeft + container.clientWidth

    if (tabLeft < viewLeft) {
      container.scrollTo({ behavior: 'smooth', left: tabLeft })
    } else if (tabRight > viewRight) {
      container.scrollTo({ behavior: 'smooth', left: tabRight - container.clientWidth })
    }
  }

  private setup(root: HTMLElement): void {
    const tabs = Array.from(getElementChildren(root, 'tab-button')) as HTMLButtonElement[]
    const panels = getElementChildren(root, 'tab-panel')
    const indicator = getElementChild(root, 'tab-indicator')

    if (tabs.length === 0) return

    const activate = (target: string, focus = false): void => {
      tabs.forEach((tab: HTMLButtonElement): void => {
        const isActive = tab.dataset.target === target
        tab.classList.toggle(IS_ACTIVE, isActive)
        tab.setAttribute('aria-selected', String(isActive))
        tab.tabIndex = isActive ? 0 : -1
        if (isActive && focus) tab.focus()
      })

      panels.forEach((panel: HTMLElement): void => {
        const isActive = panel.dataset.name === target
        panel.classList.toggle(IS_ACTIVE, isActive)
        panel.toggleAttribute('hidden', !isActive)
      })

      const active = tabs.find((tab: HTMLButtonElement): boolean => tab.dataset.target === target)
      if (active) {
        if (indicator) this.moveIndicator(indicator, active)
        this.scrollTabIntoView(active)
      }

      root.dispatchEvent(new CustomEvent('tab:change', { bubbles: true, detail: { target } }))
    }

    tabs.forEach((tab: HTMLButtonElement, index: number): void => {
      tab.addEventListener('click', (): void => {
        const target = tab.dataset.target
        if (target) activate(target)
      })

      tab.addEventListener('keydown', (event: KeyboardEvent): void => {
        const next = this.resolveKeyTarget(event.key, index, tabs.length)
        if (next === null) return

        event.preventDefault()
        const target = tabs[next]?.dataset.target
        if (target) activate(target, true)
      })
    })

    const initial = tabs.find((tab: HTMLButtonElement): boolean => tab.classList.contains(IS_ACTIVE)) ?? tabs[0]

    if (initial?.dataset.target) {
      requestAnimationFrame((): void => activate(initial.dataset.target as string))
    }

    if (indicator) {
      window.addEventListener('resize', (): void => {
        const current = tabs.find((tab: HTMLButtonElement): boolean => tab.classList.contains(IS_ACTIVE))
        if (current) this.moveIndicator(indicator, current)
      })
    }
  }
}

const tab = new Tab()
tab.init()
